import moment from "moment-timezone";
import type { TaggedRiskRow } from "@/lib/api/riskV3";
import { formatNumberToDisplay } from "@/lib/api/common";
import { CommonDestinationTimezone } from "@/lib/utils";

// ── Helpers ────────────────────────────────────────────────────────

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-medium text-muted-foreground leading-tight">{label}</span>
      <span className="text-xs leading-tight">{value}</span>
    </div>
  );
}

function fmt(val: string | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return val;
}

// ── ReleaseCard ────────────────────────────────────────────────────

interface ReleaseCardProps {
  row: TaggedRiskRow;
  customerNameMap: Record<string, string>;
}

export function ReleaseCard({ row, customerNameMap }: ReleaseCardProps) {
  const release = row.release_data;
  const detail = row.detailed_contract_info;
  const customerName = row.customer_id ? customerNameMap[row.customer_id] ?? row.customer_id : "—";
  const isCanola = /cnl|canola/i.test(row.meta.commodity_group || row.meta.commodity_display_name || "");
  const priceOpts = isCanola ? { minimumFractionDigits: 4, maximumFractionDigits: 4 } : {};

  return (
    <div className="rounded-lg border bg-white p-3 shadow-sm space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold">
          {release?.contract_number ?? "—"}{release?.contract_release ? ` / ${release.contract_release}` : ""}
        </span>
        {detail?.contract_type_label && (
          <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-medium">
            {detail.contract_type_label}
          </span>
        )}
        {release?.option_month && (
          <span className="text-[10px] text-muted-foreground">{release.option_month}</span>
        )}
      </div>

      {/* Main fields */}
      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <Field label="Customer" value={customerName} />
        <Field label="Commodity" value={fmt(row.meta.commodity_display_name)} />
        <Field label="Quantity" value={formatNumberToDisplay(row.quantity)} />
        <Field label="Futures" value={formatNumberToDisplay(row.futures, priceOpts)} />
      </div>

      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <Field label="Basis" value={formatNumberToDisplay(row.basis, priceOpts)} />
        <Field label="Price" value={formatNumberToDisplay(row.price, priceOpts)} />
        <Field label="Revenue" value={formatNumberToDisplay(row.revenue_value, priceOpts)} />
        <Field label="Status" value={fmt(release?.status)} />
      </div>

      {/* Implied values */}
      {row.meta.has_implied_values && (
        <div className="grid grid-cols-4 gap-x-4 gap-y-1">
          <Field label="Implied Futures" value={formatNumberToDisplay(row.implied_futures, { minimumFractionDigits: 4, maximumFractionDigits: 4 })} />
          <Field label="Implied Basis" value={formatNumberToDisplay(row.implied_basis, { minimumFractionDigits: 4, maximumFractionDigits: 4 })} />
          {row.meta.requires_fx_conversion && (
            <>
              <Field label="Implied FX Futures" value={formatNumberToDisplay(row.implied_fx_futures, { minimumFractionDigits: 4, maximumFractionDigits: 4 })} />
              <Field label="FX Contract" value={fmt(row.implied_fx_futures_contract)} />
            </>
          )}
        </div>
      )}

      {/* OTC legs */}
      {detail && (detail.step_one || detail.step_two || detail.step_three || detail.double_up) && (
        <div className="grid grid-cols-4 gap-x-4 gap-y-1 border-t pt-1">
          <Field label={detail.leg_labels?.["step_one"] ?? "Leg 1"} value={formatNumberToDisplay(detail.step_one, { passthroughEmpty: true })} />
          <Field label={detail.leg_labels?.["step_two"] ?? "Leg 2"} value={formatNumberToDisplay(detail.step_two, { passthroughEmpty: true })} />
          <Field label={detail.leg_labels?.["step_three"] ?? "Leg 3"} value={formatNumberToDisplay(detail.step_three, { passthroughEmpty: true })} />
          <Field label="Double Up" value={formatNumberToDisplay(detail.double_up, { passthroughEmpty: true })} />
        </div>
      )}

      {/* Dates */}
      {release && (release.start_date || release.end_date) && (
        <div className="grid grid-cols-4 gap-x-4 gap-y-1">
          <Field
            label="Contract Type"
            value={detail?.contract_type_label ?? release.contract_type ?? ""}
          />
          <Field
            label="Start Date"
            value={release.start_date ? moment.tz(release.start_date, CommonDestinationTimezone).format("MM/DD/YYYY") : "—"}
          />
          <Field
            label="End Date"
            value={release.end_date ? moment.tz(release.end_date, CommonDestinationTimezone).format("MM/DD/YYYY") : "—"}
          />
        </div>
      )}
    </div>
  );
}
